/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import type { User } from "@/types";
import { AppConfig } from "@/util/AppConfig";

// @ts-expect-error import.meta.env will be available
const endpoint = import.meta.env.VITE_BIRDHOUSE_ENDPOINT;

// Define search params interface
export interface UserSearchParams {
  name?: string;
  email?: string;
  org?: string | number;
  role?: string;
}

// Define API response interfaces
export interface ListUsersResponse {
  users: User[];
  total_count: number;
}

export function getAvailableRoles(user: User): string[] {
  const result: string[] = [];
  if (user.roles.includes("sys_admin")) {
    result.push("experimental");
    result.push("sys_admin");
  }
  if (user.roles.includes("sys_admin") || user.roles.includes("sales")) {
    result.push("sales");
    result.push("medical_analyst");
  }
  result.push("org_admin");
  result.push("operator");
  result.push("medical_practitioner");
  if (AppConfig.allowReviewer) {
    result.push("reviewer");
  }
  return result;
}

export async function listUsers(
  limit: number = 0,
  offset: number = 0,
  search: UserSearchParams | null = null
): Promise<ListUsersResponse> {
  const params: Record<string, any> = { limit, offset };
  if (search?.name) params["name"] = search.name;
  if (search?.email) params["email"] = search.email;
  if (search?.org) params["org"] = search.org;
  if (search?.role) params["roles"] = search.role;
  const response = await axios.get<ListUsersResponse>(`${endpoint}/users`, {
    params,
  });
  return response.data;
}

export async function getReviewers(): Promise<ListUsersResponse> {
  const params: Record<string, any> = { roles: "reviewer" };
  const response = await axios.get<ListUsersResponse>(`${endpoint}/users`, {
    params,
  });
  return response.data;
}

export async function getCurrentUser(): Promise<User> {
  const response = await axios.get<User>(`${endpoint}/users/current`);
  return response.data;
}

export function updateCurrentUser(
  name: string,
  phone: string,
  qualifications: string
): Promise<any> {
  return axios.patch(
    `${endpoint}/users/current`,
    JSON.stringify({
      name,
      phone,
      qualifications,
    }),
    { headers: { "Content-Type": "application/json" } }
  );
}

export function addUser(data: Partial<User>): Promise<{ id: number }> {
  return axios.post(`${endpoint}/users`, JSON.stringify(data), {
    headers: { "Content-Type": "application/json" },
  });
}

export async function updateUser(
  id: string,
  data: Partial<User>
): Promise<void> {
  await axios.patch(`${endpoint}/users/${id}`, JSON.stringify(data), {
    headers: { "Content-Type": "application/json" },
  });
}

export function deleteUser(id: string): Promise<any> {
  return axios.delete(`${endpoint}/users/${id}`);
}

export async function getUser(id: string): Promise<User> {
  const response = await axios.get<User>(`${endpoint}/users/${id}`);
  return response.data;
}

export function getAllRoles(): string[] {
  return getAvailableRoles({ roles: ["sys_admin"] } as User);
}
